import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';

export type UploadProgress = (event: { loaded: number; total?: number }) => void;

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean | undefined;
  placeholder?: string;
  minHeight?: string;
  onUploadImage?: (file: File, onProgress?: UploadProgress) => Promise<{ url: string }>;
  onUploadDocument?: (
    file: File,
    onProgress?: UploadProgress
  ) => Promise<{ url: string; originalName: string }>;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean | undefined;
  disabled?: boolean | undefined;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  active,
  disabled,
  title,
  children
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`btn btn-xs ${active ? 'btn-primary' : 'btn-ghost'}`}
  >
    {children}
  </button>
);

interface ToolbarProps {
  editor: Editor;
  disabled?: boolean | undefined;
  onPickImage?: (() => void) | undefined;
  onPickDocument?: (() => void) | undefined;
  isUploading?: boolean | undefined;
}

const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  disabled,
  onPickImage,
  onPickDocument,
  isUploading
}) => {
  const promptForLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL ссылки', previousUrl ?? 'https://');

    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' })
      .run();
  }, [editor]);

  const buttonsDisabled = disabled || isUploading;

  return (
    <div className="sticky top-0 z-10 flex flex-wrap gap-1 p-2 border-b bg-base-200 rounded-t">
      <ToolbarButton
        title="Жирный (Ctrl+B)"
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        disabled={buttonsDisabled}
      >
        <i className="fas fa-bold" />
      </ToolbarButton>
      <ToolbarButton
        title="Курсив (Ctrl+I)"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        disabled={buttonsDisabled}
      >
        <i className="fas fa-italic" />
      </ToolbarButton>
      <span className="divider divider-horizontal mx-0" />
      <ToolbarButton
        title="Заголовок 2"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        disabled={buttonsDisabled}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        title="Заголовок 3"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        disabled={buttonsDisabled}
      >
        H3
      </ToolbarButton>
      <ToolbarButton
        title="Заголовок 4"
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        active={editor.isActive('heading', { level: 4 })}
        disabled={buttonsDisabled}
      >
        H4
      </ToolbarButton>
      <ToolbarButton
        title="Заголовок 5"
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        active={editor.isActive('heading', { level: 5 })}
        disabled={buttonsDisabled}
      >
        H5
      </ToolbarButton>
      <span className="divider divider-horizontal mx-0" />
      <ToolbarButton
        title="Маркированный список"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        disabled={buttonsDisabled}
      >
        <i className="fas fa-list-ul" />
      </ToolbarButton>
      <ToolbarButton
        title="Нумерованный список"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        disabled={buttonsDisabled}
      >
        <i className="fas fa-list-ol" />
      </ToolbarButton>
      <span className="divider divider-horizontal mx-0" />
      <ToolbarButton
        title="Добавить/изменить ссылку"
        onClick={promptForLink}
        active={editor.isActive('link')}
        disabled={buttonsDisabled}
      >
        <i className="fas fa-link" />
      </ToolbarButton>
      <ToolbarButton
        title="Убрать ссылку"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={buttonsDisabled || !editor.isActive('link')}
      >
        <i className="fas fa-unlink" />
      </ToolbarButton>
      {(onPickImage || onPickDocument) && (
        <span className="divider divider-horizontal mx-0" />
      )}
      {onPickImage && (
        <ToolbarButton
          title="Вставить изображение"
          onClick={onPickImage}
          disabled={buttonsDisabled}
        >
          <i className="fas fa-image" />
        </ToolbarButton>
      )}
      {onPickDocument && (
        <ToolbarButton
          title="Вставить документ (PDF)"
          onClick={onPickDocument}
          disabled={buttonsDisabled}
        >
          <i className="fas fa-file-pdf" />
        </ToolbarButton>
      )}
      <span className="divider divider-horizontal mx-0" />
      <ToolbarButton
        title="Очистить форматирование"
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
        disabled={buttonsDisabled}
      >
        <i className="fas fa-eraser" />
      </ToolbarButton>
      <span className="divider divider-horizontal mx-0" />
      <ToolbarButton
        title="Отменить (Ctrl+Z)"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={buttonsDisabled || !editor.can().undo()}
      >
        <i className="fas fa-undo" />
      </ToolbarButton>
      <ToolbarButton
        title="Вернуть (Ctrl+Shift+Z)"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={buttonsDisabled || !editor.can().redo()}
      >
        <i className="fas fa-redo" />
      </ToolbarButton>
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
  minHeight = '400px',
  onUploadImage,
  onUploadDocument
}) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<{
    busy: boolean;
    progress: number;
    error: string | null;
  }>({ busy: false, progress: 0, error: null });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4, 5] }
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'link link-primary'
        }
      }),
      Image.configure({
        inline: false,
        HTMLAttributes: { class: 'rounded my-4 max-w-full h-auto' }
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Начните вводить содержимое...',
        emptyEditorClass: 'is-editor-empty'
      })
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'content-html max-w-none focus:outline-none p-4',
        style: `min-height: ${minHeight}`
      }
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    }
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const handleImageFile = useCallback(
    async (file: File) => {
      if (!editor || !onUploadImage) return;
      setUploadState({ busy: true, progress: 0, error: null });
      try {
        const { url } = await onUploadImage(file, (event) => {
          const progress = event.total
            ? Math.round((event.loaded * 100) / event.total)
            : 0;
          setUploadState((s) => ({ ...s, progress }));
        });
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
        setUploadState({ busy: false, progress: 0, error: null });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Ошибка загрузки изображения';
        setUploadState({ busy: false, progress: 0, error: message });
      }
    },
    [editor, onUploadImage]
  );

  const handleDocumentFile = useCallback(
    async (file: File) => {
      if (!editor || !onUploadDocument) return;
      setUploadState({ busy: true, progress: 0, error: null });
      try {
        const { url, originalName } = await onUploadDocument(file, (event) => {
          const progress = event.total
            ? Math.round((event.loaded * 100) / event.total)
            : 0;
          setUploadState((s) => ({ ...s, progress }));
        });
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: originalName,
            marks: [
              {
                type: 'link',
                attrs: {
                  href: url,
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }
              }
            ]
          })
          .run();
        setUploadState({ busy: false, progress: 0, error: null });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Ошибка загрузки документа';
        setUploadState({ busy: false, progress: 0, error: message });
      }
    },
    [editor, onUploadDocument]
  );

  if (!editor) return null;

  return (
    <div className="border rounded focus-within:ring-1 focus-within:ring-primary/40 transition-shadow">
      <Toolbar
        editor={editor}
        disabled={disabled}
        onPickImage={
          onUploadImage ? () => imageInputRef.current?.click() : undefined
        }
        onPickDocument={
          onUploadDocument ? () => documentInputRef.current?.click() : undefined
        }
        isUploading={uploadState.busy}
      />
      <EditorContent editor={editor} />
      {(uploadState.busy || uploadState.error) && (
        <div className="border-t bg-base-200 px-3 py-2 text-sm">
          {uploadState.busy && (
            <div className="flex items-center gap-2 text-primary">
              <span className="loading loading-spinner loading-xs" />
              <span>Загрузка... {uploadState.progress}%</span>
            </div>
          )}
          {uploadState.error && (
            <div className="flex items-center justify-between gap-2 text-error">
              <span>
                <i className="fas fa-triangle-exclamation mr-2" />
                {uploadState.error}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() =>
                  setUploadState({ busy: false, progress: 0, error: null })
                }
              >
                Закрыть
              </button>
            </div>
          )}
        </div>
      )}
      {onUploadImage && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void handleImageFile(file);
          }}
        />
      )}
      {onUploadDocument && (
        <input
          ref={documentInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void handleDocumentFile(file);
          }}
        />
      )}
    </div>
  );
};
